import { create } from 'zustand';
import type { TreeNode } from '@/types';
import { findHighlightPath } from '../pages/mindmap/flowUtils';
import { fetchTree } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers (moved from MindMapFlow)
// ---------------------------------------------------------------------------

/** Recursively remove a node and all its descendants from the set. */
function collapseNodeAndDescendants(nodeSet: Set<string>, node: TreeNode) {
  nodeSet.delete(node.id);
  if (node.children) {
    node.children.forEach((child) => collapseNodeAndDescendants(nodeSet, child));
  }
}

/** Walk the tree to find siblings (and parent) of a given node id. */
function findSiblings(
  root: TreeNode,
  targetId: string,
): { siblings: TreeNode[]; parent: TreeNode | null } {
  if (root.children) {
    for (const child of root.children) {
      if (child.id === targetId) {
        return {
          siblings: root.children.filter((c) => c.id !== targetId),
          parent: root,
        };
      }
      const result = findSiblings(child, targetId);
      if (result.siblings.length > 0 || result.parent) return result;
    }
  }
  return { siblings: [], parent: null };
}

// ---------------------------------------------------------------------------
// Store types
// ---------------------------------------------------------------------------

interface MindMapState {
  /** Raw tree data from the API. */
  data: TreeNode | null;
  /** Loading flag for the current fetch. */
  loading: boolean;
  /** Error message when a fetch fails. */
  error: string | null;
  /** Set of expanded node IDs. */
  expandedNodes: Set<string>;
  /** Whether the maximum platform gap highlight mode is on. */
  showHighlightPath: boolean;
  /** Current batch identifier (e.g. "202501"). */
  // selectedBatch: string;

  // Actions
  // setSelectedBatch: (batch: string) => void;
  fetchTreeData: (batch: string) => Promise<void>;
  toggleNode: (nodeId: string) => void;
  collapseAll: () => void;
  toggleHighlightPath: () => void;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useMindMapStore = create<MindMapState>((set, get) => ({
  data: null,
  loading: true,
  error: null,
  expandedNodes: new Set<string>(),
  showHighlightPath: true,
  // selectedBatch: '',

  // ---- Actions ----------------------------------------------------------

  // setSelectedBatch: (batch: string) => {
  //   set({ selectedBatch: batch });
  // },

  fetchTreeData: async (batch: string) => {
    const state = get();
    if (!batch) return;
    set({ loading: true, error: null });
    try {
      const batchId = batch.replace('/', '');
      const tree = await fetchTree(batchId);
      const showHighlightPath = state.showHighlightPath;
      let expandedNodes: Set<string>;
      if (showHighlightPath) {
        expandedNodes = findHighlightPath(tree);
      } else {
        expandedNodes = new Set([tree.id]);
      }
      set({ data: tree, expandedNodes, loading: false });
    } catch (err: any) {
      set({ error: err?.message || 'Failed to load tree', loading: false });
    }
  },

  toggleNode: (nodeId: string) => {
    const { data, expandedNodes } = get();
    if (!data) return;

    // Find the node in the tree
    const findNode = (root: TreeNode, id: string): TreeNode | null => {
      if (root.id === id) return root;
      if (root.children) {
        for (const child of root.children) {
          const found = findNode(child, id);
          if (found) return found;
        }
      }
      return null;
    };

    const nodeData = findNode(data, nodeId);
    if (!nodeData) return;

    const wasExpanded = expandedNodes.has(nodeId);

    if (wasExpanded) {
      // Collapse this node and its descendants
      const next = new Set(expandedNodes);
      collapseNodeAndDescendants(next, nodeData);
      set({ expandedNodes: next });
    } else {
      // Expand: add this node, collapse all siblings
      const next = new Set(expandedNodes);
      const { siblings } = findSiblings(data, nodeId);
      siblings.forEach((sibling) => collapseNodeAndDescendants(next, sibling));
      next.add(nodeId);
      set({ expandedNodes: next });
    }
  },

  collapseAll: () => {
    const { data } = get();
    if (data) {
      set({ expandedNodes: new Set([data.id]) });
    }
  },

  toggleHighlightPath: () => {
    const { showHighlightPath, data } = get();
    const newState = !showHighlightPath;
    if (newState && data) {
      const path = findHighlightPath(data);
      set({ showHighlightPath: newState, expandedNodes: path });
    } else {
      set({ showHighlightPath: newState });
    }
  },
}));
